import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2,
  ScanLine,
  Check,
  ChevronDown,
  ChevronUp,
  Link as LinkIcon,
  Clock,
  AlertTriangle,
  Copy,
  CircleX,
  FileUp,
  Pause,
  RefreshCw,
  Sparkles,
  Unplug,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DocTypeChip, LinkStatusChip } from './DocChips';
import {
  buildReviewTargetIndex,
  resolveReviewRowTarget,
  type ReviewTab,
} from '../config/reviewTabs';
import type { DocStreamConnectionState } from '../lib/documentCaptureStream';
import type { JourneyRow } from '../store/documentCaptureStore';
import {
  type DocLinkStatus,
  type DocStreamPhase,
  type ReviewQueueItem,
} from '../types/documentCapture.types';

/**
 * The live journey (86d3rrfbf core principle 1): ONE row per file, morphing in place through
 * `Uploading… → Reading barcode… → captured ✓ → linked ✓`. Never a row per pipeline step, and
 * never a toast — the calm is the product. Success is quiet; only warning and destructive carry
 * visual weight.
 */

interface PhaseConfig {
  label: string;
  icon: LucideIcon;
  tone: 'muted' | 'info' | 'success' | 'warning' | 'destructive';
  spin?: boolean;
  pulse?: boolean;
}

const PHASES: Record<DocStreamPhase, PhaseConfig> = {
  uploading: { label: 'Uploading…', icon: Loader2, tone: 'muted', spin: true },
  reading: { label: 'Reading barcode…', icon: ScanLine, tone: 'muted', spin: true },
  // 86d3x48q7 T13 — the second reading is a visible STEP, not a hidden one. Verification happens
  // after decode, and showing it is how an operator learns the pipeline's shape by watching; a scan
  // that silently paused between "read" and "captured" would just look slow.
  verifying: { label: 'Checking it independently…', icon: Sparkles, tone: 'info', spin: true },
  // 86d3yeh4j — a transient failure being retried. MUTED, not warning: this is ordinary contention
  // and nothing is wrong yet, so it must not look like something needing attention. The label is
  // completed per-row with the try count below — "trying again" with no number invites the very
  // question ("is it stuck?") the row exists to answer.
  retrying: { label: 'The check timed out — trying again', icon: RefreshCw, tone: 'muted', spin: true },
  // 86d3yeh4j — parked while the reader is down for everyone. Warning tone (something IS wrong, and
  // the banner above says what), but explicitly NOT spinning: a spinner would promise progress that
  // is not happening. "nothing to do" is the whole message — the alternative, before this ticket,
  // was this scan sitting in someone's review queue.
  held: { label: 'Waiting for the second reader to come back — nothing to do', icon: Pause, tone: 'warning' },
  captured: { label: 'Captured', icon: Check, tone: 'success' },
  linked: { label: 'Linked', icon: LinkIcon, tone: 'success' },
  waiting: { label: 'Waiting for NetSuite', icon: Clock, tone: 'info', pulse: true },
  review: { label: 'Needs review', icon: AlertTriangle, tone: 'warning' },
  duplicate: { label: 'Already captured', icon: Copy, tone: 'muted' },
  failed: { label: 'Failed', icon: CircleX, tone: 'destructive' },
  updated: { label: 'Updated', icon: FileUp, tone: 'success' },
};

/**
 * Phases whose row carries a number that has NOT yet become a document. Hoisted to module scope: it
 * is a constant, and rebuilding it per row on every render of a 50-row feed is pure waste.
 */
const PENDING_IDENTIFICATION: DocStreamPhase[] = ['review', 'verifying', 'retrying', 'held'];

const TONE_TEXT: Record<PhaseConfig['tone'], string> = {
  muted: 'text-muted-foreground',
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  destructive: 'text-destructive',
};

interface LiveStreamPanelProps {
  rows: JourneyRow[];
  connection: DocStreamConnectionState;
  /** Manual retry once the reconnect budget is spent (86d3y0cku). */
  onReconnect: () => void;
  /**
   * 86d3z2nz4 — the OPEN review queue, already in memory. A `review` row resolves its item from this
   * at render time, so the click opens the queue at that item with no network round trip in front of
   * the first visible change (CLAUDE.md responsiveness gate).
   */
  reviewItems: ReviewQueueItem[];
  /**
   * `docscan.review`. Without it the rows stay inert rather than offering a control that would 403 —
   * a dead affordance is worse than none. Passed as a VALUE from the page, never a permission getter
   * read in render (CLAUDE.md 86d3d1z40).
   */
  canReview: boolean;
  onOpenReview: (tab: ReviewTab, reviewItemId?: number) => void;
}

/**
 * How a row is named in the accessible label of its review control.
 *
 * ⚠️ The fallback is spelled out on purpose. Every unidentified row would otherwise share ONE label
 * ("Open review for a scan"), which is a mistake this feature has already made and documented — five
 * indistinguishable rows in the same list is not a name, it is a category. The file id is what tells
 * them apart, and it is the same spelling the review overlay uses for the same scan.
 *
 * The order deliberately mirrors the row's own VISIBLE label (`docNumber ?? fileName ?? 'Scan'`), so
 * the accessible name always contains what a person can read on the row (WCAG 2.5.3).
 */
function rowName(row: JourneyRow): string {
  if (row.docNumber) return row.docNumber;
  if (row.fileName) return row.fileName;
  if (row.fileId != null) return `scan #${row.fileId}`;
  return 'an unidentified scan';
}

/**
 * 86d3y0cku — the live feed gave up reconnecting. Shown ABOVE the rows rather than only in the empty
 * state, because that is the dangerous case: a screen full of rows that stopped updating looks
 * exactly like a screen full of rows that is up to date, and an operator would keep scanning into
 * what they believe is a live feed. The bounded retry budget is what makes this notice necessary —
 * before it, the feed retried forever and never had to admit anything.
 */
function OfflineNotice({ onReconnect }: { onReconnect: () => void }) {
  return (
    <div
      className="flex items-start gap-3 border-b border-warning/25 bg-warning/10 px-4 py-3"
      role="status"
    >
      <Unplug className="mt-0.5 h-4 w-4 flex-shrink-0 text-warning" aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-foreground">Live updates stopped</p>
        <p className="mt-0.5 text-sm text-muted-foreground">
          DOP could not reach the live feed. Captures still upload — this list just will not move
          until the connection is back.
        </p>
      </div>
      <Button size="sm" variant="outline" onClick={onReconnect} className="flex-shrink-0">
        Reconnect
      </Button>
    </div>
  );
}

/**
 * E7 item 6 — the quiet counterpart to OfflineNotice, shown while the stream is still trying.
 *
 * Deliberately muted rather than a warning: this state is usually transient (an API restart clears in
 * seconds) and dressing it as a problem would cry wolf several times a day. The warning treatment stays
 * reserved for OfflineNotice, which means "we have stopped trying".
 *
 * Says the same three things OfflineNotice gets right — what stopped, what is still safe, what happens
 * next — and deliberately claims NO cause: the browser cannot read a status code or body off a failed
 * EventSource, so the page genuinely cannot tell an API restart from a disabled module from a dropped
 * connection. A confident wrong explanation would be worse than none.
 *
 * `role="status"` (not inside the list's aria-live region) so it is announced once as a status change
 * rather than as a row being added to the feed.
 */
function ReconnectingNotice() {
  return (
    <div
      className="flex items-center gap-2.5 border-b border-border bg-muted/40 px-4 py-2"
      role="status"
    >
      <Loader2 className="h-3.5 w-3.5 flex-shrink-0 animate-spin text-muted-foreground" aria-hidden />
      <p className="text-xs text-muted-foreground">
        Reconnecting to the live feed — captures still upload, this list may be behind.
      </p>
    </div>
  );
}

/**
 * 86d3yeh5j — the one control that says where the finished rows went, and puts them back.
 *
 * Rendered as a SIBLING of the aria-live `<ul>`, never a child of it: a `<button>` is invalid as a
 * `ul` child, and inside the live region `aria-relevant="text"` would make a screen reader announce
 * the count every time a row stepped aside. The tooltip is the only place the behaviour is explained,
 * so it is not optional — a list that quietly drops rows is exactly the thing operators stop trusting.
 */
function RetiredFooter({
  count,
  open,
  onToggle,
}: {
  count: number;
  open: boolean;
  onToggle: () => void;
}) {
  const Chevron = open ? ChevronUp : ChevronDown;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex w-full items-center justify-center gap-1.5 border-t border-border bg-muted/35 px-4 py-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground"
        >
          <Chevron className="h-3 w-3" aria-hidden />
          {open ? `Hide the ${count} finished` : `${count} finished and filed · show`}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-xs">
        Finished scans step aside after a moment so this list stays about what is happening and what
        needs you. They are all in All documents.
      </TooltipContent>
    </Tooltip>
  );
}

export function LiveStreamPanel({
  rows,
  connection,
  onReconnect,
  reviewItems,
  canReview,
  onOpenReview,
}: LiveStreamPanelProps) {
  // 86d3yeh5j — LOCAL state, not store state: expanding is a private, momentary act of looking, and
  // the only thing it changes is this list. Held here, the toggle re-renders the panel and nothing
  // else; in the store it would re-render the whole capture page (CLAUDE.md re-render gate).
  const [showRetired, setShowRetired] = useState(false);

  // 86d3z2nz4 — one pass over the queue per queue change, not one scan per row per render.
  const reviewIndex = useMemo(() => buildReviewTargetIndex(reviewItems), [reviewItems]);

  let retiredCount = 0;
  for (const row of rows) if (row.retired) retiredCount += 1;
  // Expanding restores them IN PLACE — the same list, in the same order, with the gaps filled back
  // in. Appending them at the bottom would break the one thing the feed is good at: reading top-down
  // as a timeline.
  const visible = showRetired || retiredCount === 0 ? rows : rows.filter((row) => !row.retired);
  const toggleRetired = () => setShowRetired((open) => !open);

  if (visible.length === 0) {
    return (
      <>
        <div className="p-2">
          {connection === 'offline' ? (
            <EmptyState
              icon={Unplug}
              title="Live updates stopped"
              description="DOP could not reach the live feed. Captures still upload — this list just will not move until the connection is back."
              action={
                <Button size="sm" variant="outline" onClick={onReconnect}>
                  Reconnect
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={ScanLine}
              title="Nothing scanning right now"
              description={
                connection === 'open'
                  ? 'Scans appear here the moment they land in the watched folder — no refresh needed.'
                  : 'Connecting to the live stream…'
              }
            />
          )}
        </div>
        {/* Everything finished and stepped aside. The empty state is the truth — nothing IS
            happening — but the footer has to stay, or the day's work looks deleted. */}
        {retiredCount > 0 && (
          <RetiredFooter count={retiredCount} open={showRetired} onToggle={toggleRetired} />
        )}
      </>
    );
  }

  return (
    <>
      {connection === 'offline' && <OfflineNotice onReconnect={onReconnect} />}
      {/* E7 item 6 — the gap before OfflineNotice. With rows already on screen, a dropped stream showed
          NOTHING for the whole ~181s reconnect budget (1+2+4+8+16 then 30×5), so the list looked live
          while it had stopped moving — the exact confusion OfflineNotice exists to prevent, just earlier.
          (The empty-list branch above already says "Connecting…", so only this branch was silent.)

          Gated on "neither open nor offline" rather than on 'reconnecting' alone: when the outage starts
          before the first successful connect, the stream alternates 'connecting' ↔ 'reconnecting' on every
          backoff tick, and keying on one of them would blink the notice on and off. */}
      {connection !== 'open' && connection !== 'offline' && <ReconnectingNotice />}
      {/* Stream updates are ambient, not urgent — polite so a screen reader isn't interrupted
          mid-sentence every time a file lands. */}
      <ul className="divide-y divide-border" aria-live="polite" aria-relevant="additions text">
      {visible.map((row) => {
        let phase = PHASES[row.phase] ?? PHASES.uploading;
        // W9 (86d3vbwup) — a deliberate replace emits `reading` WITH a docNumber (a fresh capture has
        // none until it's identified). Same phase, replace-flavoured copy — no new phase names.
        if (row.phase === 'reading' && row.docNumber)
          phase = { ...phase, label: 'Updating — reading the file…' };
        // 86d3x48q7 T13 — the two checking states read differently because they mean different
        // things: with a number in hand the AI is CORROBORATING, without one it is the only reader
        // left. The bundle's copy carries the number inline ("Barcode read DR-… — checking it
        // independently…"); this row already renders the number to the left, so repeating it here
        // would print it twice.
        if (row.phase === 'verifying' && !row.docNumber)
          phase = { ...phase, label: 'The barcode reader found nothing — asking the second reader…' };
        // 86d3yeh4j — the try count, when the server sent one. Guarded rather than assumed: a
        // `retrying` event from a server that predates the counter carries neither number, and
        // printing "(undefined of undefined)" on a live feed is worse than printing nothing.
        if (row.phase === 'retrying' && row.attempt != null && row.of != null)
          phase = { ...phase, label: `The check timed out — trying again (${row.attempt} of ${row.of})` };
        // 86d3ygnz3 — a cap hold is not an outage. The default copy ("waiting for the second reader to
        // come back") names a fault that does not exist when the reader is perfectly healthy and the
        // day's budget is simply spent, and it points the operator at the wrong fix.
        if (row.phase === 'held' && row.holdReason === 'quota')
          phase = { ...phase, label: 'Today’s check budget is used up — waiting for the reset' };
        // 86d3x48q7 T13 — a scan a PERSON identified in review. The row must not read as though the
        // machine did it: the readers failed on this one, and the feed is the only place that says so.
        if (row.resolvedByYou && row.phase === 'waiting')
          phase = { ...phase, label: 'You confirmed it — writing the link into NetSuite…', spin: true, pulse: false };
        if (row.resolvedByYou && row.phase === 'linked')
          phase = { ...phase, label: 'Confirmed by you · linked' };
        const Icon = phase.icon;
        const label = row.docNumber ?? row.fileName ?? 'Scan';

        const content = (
          <span className="flex min-w-0 flex-1 items-center gap-3">
            <Icon
              className={cn(
                'h-4 w-4 flex-shrink-0',
                TONE_TEXT[phase.tone],
                phase.spin && 'motion-safe:animate-spin',
                phase.pulse && 'motion-safe:animate-pulse'
              )}
              aria-hidden
            />
            {row.prefix && <DocTypeChip value={row.prefix} />}
            <span className="truncate font-mono text-sm text-foreground">{label}</span>
            {/* A duplicate carries the existing document's link state (86d3uf18j L2) so the row is
                more than a bare "Already captured" — null (treasury, no access) falls back to text. */}
            {row.phase === 'duplicate' && row.duplicateStatus?.linkStatus != null ? (
              <span className="ml-auto flex flex-shrink-0 items-center gap-2">
                <span className={cn('text-xs', TONE_TEXT[phase.tone])}>{phase.label}</span>
                <LinkStatusChip status={row.duplicateStatus.linkStatus as DocLinkStatus} />
              </span>
            ) : (
              <span className={cn('ml-auto flex-shrink-0 text-xs', TONE_TEXT[phase.tone])}>{phase.label}</span>
            )}
          </span>
        );

        // A row is only a viewer link once a real DOCUMENT exists behind the number, and having a
        // number is NOT that: `review` rows carry the second reader's SUGGESTION (nothing attached —
        // the document row is only created when a person confirms), and `verifying` rows carry a
        // barcode read still being corroborated. Linking those to /d/{number} lands on "Document not
        // found" (prod, 2026-08-05: DR-000044843). They stay inert; the Needs-review rail is the
        // actionable surface for them.
        //
        // 86d3yeh4j — `retrying` and `held` are the SAME barcode read, still uncorroborated, just
        // stalled. They carry a number for exactly as long as `verifying` does and attach no sooner,
        // so leaving them out of this list would reintroduce the 404 for every scan caught in an
        // outage — which during an outage is all of them.
        const isRealDocument = row.docNumber != null && !PENDING_IDENTIFICATION.includes(row.phase);

        // 86d3z2nz4 — a `review` row opens the review queue AT ITS ITEM. Only `review`: `verifying`,
        // `retrying` and `held` are the same uncorroborated read still in flight, with no queue item
        // behind them and no document to open, so they stay inert exactly as PR #460 left them.
        //
        // Resolved HERE, at render, from the queue already in memory — which is also what makes a
        // stale row harmless. `RejectScanAsync` and `KeepWatchingAsync` publish nothing, deliberately,
        // so a row stays at `review` indefinitely after its item is gone; with the target resolved per
        // render the control simply is not there once the item leaves the queue, instead of opening
        // somebody else's paper.
        const reviewTarget =
          row.phase === 'review' && canReview ? resolveReviewRowTarget(row, reviewIndex) : null;

        return (
          // 86d3yeh5j — a retired row opts OUT of the surrounding live region (`aria-live` applies
          // from the nearest ancestor-or-self). Without this, expanding the footer would dump up to
          // fifty finished rows into a polite live region at once and a screen reader would read the
          // lot — the user asked to LOOK at old rows, not to be told about them.
          <li key={row.key} aria-live={row.retired ? 'off' : undefined}>
            {/* A captured row is a real document — make it a deep link to the viewer.
                Anything pre-identification has nothing to link to yet. */}
            {isRealDocument ? (
              <Link
                to={`/d/${row.docNumber}`}
                className="flex items-center px-4 py-2.5 transition-colors hover:bg-accent"
              >
                {content}
              </Link>
            ) : reviewTarget ? (
              // ONE hit area for the whole row (86d3z2nz4) — a dense feed row must not grow a second
              // control beside the first. The route back to the DOCUMENT, which a document-level
              // review row used to carry here, now lives in the overlay this opens.
              //
              // ⚠️ The accessible name must NOT start with "Review: " — that is the Needs-review
              // rail's label, the live stream renders FIRST, and `e2e/helpers.ts openReviewOverlay`
              // takes the first match. A row labelled the same way would silently capture all eleven
              // of its call sites and they would stay green while testing nothing.
              <button
                type="button"
                onClick={() => onOpenReview(reviewTarget.tab, reviewTarget.reviewItemId)}
                aria-label={`Open review for ${rowName(row)}`}
                className="flex w-full items-center px-4 py-2.5 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                {content}
              </button>
            ) : (
              <div className="flex items-center px-4 py-2.5">{content}</div>
            )}
          </li>
        );
      })}
      </ul>
      {retiredCount > 0 && (
        <RetiredFooter count={retiredCount} open={showRetired} onToggle={toggleRetired} />
      )}
    </>
  );
}
